import React from 'react'
import { FormControl, FormLabel, FormControlLabel, RadioGroup, Radio, Checkbox } from '@material-ui/core'
import { ModalScreenOverlay, OptionsPanelContainer, OptionsPanelCloseIcon } from './styled-elements'
import { CORE_MAP_METRIC_VALUE, getMapMetricCategorySelectionState, MAP_METRIC_CATEGORIES, MAP_METRIC_OPTIONS } from '../../utils/mapMetricOptions'

const metricOptionsByValue = MAP_METRIC_OPTIONS.reduce((memo, option) => {
  memo[option.value] = option
  return memo
}, {})

const sprayMetricValueSet = new Set((MAP_METRIC_CATEGORIES.find(({ id }) => id === 'spray') || {}).metricValues || [])

const harvestMetricValueSet = new Set((MAP_METRIC_CATEGORIES.find(({ id }) => id === 'harvest') || {}).metricValues || [])

const MetricCategoryDot = ({ color }) => (
  <span
    style={{
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: '6px',
    }}
  />
)

const MetricCheckboxLabel = ({ metricValue, label }) => {
  const showSprayDot = sprayMetricValueSet.has(metricValue)
  const showHarvestDot = harvestMetricValueSet.has(metricValue)

  return <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
    {showSprayDot && <MetricCategoryDot color="#2E7D32" />}
    {showHarvestDot && <MetricCategoryDot color="#C62828" />}
    <span>{label}</span>
  </span>
}

const compactCheckboxProps = {
  color: 'primary',
  size: 'small',
  style: {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
}

const compactCheckboxLabelStyle = {
  marginTop: '0',
  marginBottom: '0',
}

const compactRadioProps = {
  color: 'primary',
  size: 'small',
  style: {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
}

const sectionLabelStyle = {
  marginBottom: '8px',
}

export default class OptionsPanel extends React.Component {

  callCloseActionIfClickOutsidePopup = (event,closeOptionsPanelHandler) => {
    if (event.target === event.currentTarget) closeOptionsPanelHandler.call()
  }

  renderBody = (props,state) => {

    const {
      closeOptionsPanelHandler, mapShowAllAwsNames, mapShowAllAwsNamesChangeHandler,
      mapShowLclbStations, mapShowLclbStationsChangeHandler,
      mapShowWindArrows, mapShowWindArrowsChangeHandler,
      metricCategories, selectedMetricValues,
      metricCategoryToggleHandler, metricValueToggleHandler,
      selectAllMetricValuesHandler, clearAllMetricValuesHandler,
    } = props

    return <ModalScreenOverlay onClick={e => this.callCloseActionIfClickOutsidePopup(e,closeOptionsPanelHandler)}>
      <OptionsPanelContainer >
        <OptionsPanelCloseIcon onClick={closeOptionsPanelHandler} />
        <div style={{ maxHeight: '70vh', minWidth: '320px', paddingTop: '12px', paddingRight: '24px', paddingBottom: '16px' }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" style={sectionLabelStyle}>Map Style</FormLabel>
            <RadioGroup aria-label="map-style" name="map-style-1" value={props.mapTypeId} onChange={props.mapTypeIdChangHandler}>
              <FormControlLabel value="roadmap" control={<Radio {...compactRadioProps} />} style={compactCheckboxLabelStyle} label="Roadmap" />
              <FormControlLabel value="satellite" control={<Radio {...compactRadioProps} />} style={compactCheckboxLabelStyle} label="Satellite" />
              <FormControlLabel value="terrain" control={<Radio {...compactRadioProps} />} style={compactCheckboxLabelStyle} label="Terrain" />
            </RadioGroup>
            <div style={{height: '20px'}} />
            <FormLabel component="legend" style={sectionLabelStyle}>General</FormLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={mapShowAllAwsNames}
                  onChange={mapShowAllAwsNamesChangeHandler}
                  name="map-show-all-aws-names"
                  {...compactCheckboxProps}
                />
              }
              style={compactCheckboxLabelStyle}
              label="Show AWS Names"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={mapShowLclbStations}
                  onChange={mapShowLclbStationsChangeHandler}
                  name="map-show-lclb-stations"
                  {...compactCheckboxProps}
                />
              }
              style={compactCheckboxLabelStyle}
              label="Show LCLB Stations"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={mapShowWindArrows}
                  onChange={mapShowWindArrowsChangeHandler}
                  name="map-show-wind-arrows"
                  {...compactCheckboxProps}
                />
              }
              style={compactCheckboxLabelStyle}
              label="Show wind arrows"
            />
            <div style={{height: '20px'}} />
            <FormLabel component="legend" style={sectionLabelStyle}>Map metrics</FormLabel>
            <div style={{ color: '#666', fontSize: '14px', marginTop: '8px', marginBottom: '12px' }}>
              Core metric always included: {metricOptionsByValue[CORE_MAP_METRIC_VALUE].label}
            </div>
            <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px', lineHeight: 1.5 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MetricCategoryDot color="#2E7D32" />
                <span>Metrics particularly useful for spray operations.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MetricCategoryDot color="#C62828" />
                <span>Metrics particularly useful for harvest operations.</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
              <div style={{ color: '#2F6BE1', cursor: 'pointer', fontSize: '14px' }} onClick={selectAllMetricValuesHandler}>
                Select all metrics
              </div>
              <div style={{ color: '#2F6BE1', cursor: 'pointer', fontSize: '14px' }} onClick={clearAllMetricValuesHandler}>
                Clear all metrics
              </div>
            </div>
            {metricCategories.map((category) => {
              const categorySelectionState = getMapMetricCategorySelectionState(category.metricValues, selectedMetricValues)

              return <div key={category.id} style={{ marginTop: '4px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={categorySelectionState.checked}
                      indeterminate={categorySelectionState.indeterminate}
                      onChange={() => metricCategoryToggleHandler(category.id)}
                      {...compactCheckboxProps}
                    />
                  }
                  style={compactCheckboxLabelStyle}
                  label={category.label}
                />
                {category.metricValues
                  .filter((metricValue) => metricValue !== CORE_MAP_METRIC_VALUE)
                  .map((metricValue) => (
                    <div key={metricValue} style={{ marginLeft: '24px' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedMetricValues.includes(metricValue)}
                            onChange={() => metricValueToggleHandler(metricValue)}
                            {...compactCheckboxProps}
                          />
                        }
                        style={compactCheckboxLabelStyle}
                        label={<MetricCheckboxLabel metricValue={metricValue} label={metricOptionsByValue[metricValue].label} />}
                      />
                    </div>
                  ))}
              </div>
            })}
            <div style={{ height: '20px' }} />
          </FormControl>
        </div>
     </OptionsPanelContainer>
    </ModalScreenOverlay>
  }

  render() {
    return this.renderBody(this.props,this.state)
  }
}
